import * as React from 'react';
import { useEffect } from 'react';
import { observer } from "mobx-react-lite";
import { orderController } from "../Controllers/OrderController";
import { Button, Typography } from "@material-tailwind/react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { useNavigate, useParams } from "react-router-dom";
import { TransactionResult } from "../../../Common/DTOs/Payment/TransactionDTO";
import LoadingOverlay from "../../../Common/Components/LoadingOverlay";

export const OrderConfirmationSubpage = observer(() => {
    const CURRENT_STEP = 3;
    orderController.activeStep = CURRENT_STEP;

    const navigate = useNavigate();
    const {systemTransactionId} = useParams();
    const transactionId = Number(systemTransactionId);

    const isTerminalResult = (result?: TransactionResult) =>
      result === TransactionResult.Succeeded ||
      result === TransactionResult.SucceededNoEmail ||
      result === TransactionResult.Failed;

    useEffect(() => {
      const handleResize = () => {
        orderController.activeStep = CURRENT_STEP;
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    useEffect(() => {
      if (!transactionId) return;

      let cancelled = false;
      let timeoutId: number | undefined;
      let attempts = 0;
      const MAX_ATTEMPTS = 30;

      const poll = async () => {
        if (cancelled) return;

        try {
          await orderController.getTransaction(transactionId);
        } catch(err) {
          if (err === "CANCELLED") return;
        }
        if (cancelled) return;

        const result = orderController.transaction?.result;
        if (isTerminalResult(result)) {
          if (orderController.transactionSuccessful) {
            await orderController.finalizePayment();
          }
          return;
        }

        attempts++;
        if (attempts >= MAX_ATTEMPTS) {
          // optional: mark as failed or show "still processing"
          return;
        }

        timeoutId = window.setTimeout(poll, 1000);
      };

      poll();

      return () => {
        cancelled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, [transactionId]);

    const getMessageForResult = (result?: TransactionResult) => {
      switch (result) {
        case TransactionResult.Succeeded:
        case TransactionResult.SucceededNoEmail:
          return "Payment successful";
        case TransactionResult.Failed:
          return "Payment failed";
        default:
          return "Processing payment...";
      }
    };

    if (!orderController.transaction || !isTerminalResult(orderController.transaction.result)) {
      return <LoadingOverlay isVisible={true}/>;
    }

    return <>
      <div className="flex flex-col basis-full gap-4 justify-center items-center">
        <Typography variant={"h2"}>{getMessageForResult(orderController.transaction.result)}</Typography>
        {orderController.transactionSuccessful
          ? <CheckCircleIcon className="h-24 w-24 mdh:h-24 mdh:w-24"/>
          : <XCircleIcon className="h-24 w-24 mdh:h-24 mdh:w-24"/>
        }
        <div className={"flex flex-row justify-between gap-2"}>
          <Typography className={"font-bold"}>Transaction ID:</Typography>
          <Typography>{systemTransactionId}</Typography>
        </div>
        <div className="flex flex-row justify-center gap-2 w-full">
          {orderController.transactionSuccessful ?
            <>
              <Button
                size="md"
                className="rounded-none h-auto w-full md:w-auto !px-3 !py-2 lgh:!px-6 lgh:!py-3 hover:bg-white/90"
                color="white"
                onClick={() => navigate("/")}
              >
                <div className="flex items-center justify-center">
                  <span className="mr-2 text-sm lgh:text-xl">Home</span>
                </div>
              </Button>
              <Button
                size="md"
                className="rounded-none h-auto w-full md:w-auto !px-3 !py-2 lgh:!px-6 lgh:!py-3 hover:bg-white/90"
                color="white"
                onClick={() => navigate("/account/orders")}
              >
                <div className="flex items-center justify-center">
                  <span className="mr-2 text-sm lgh:text-xl">My Orders</span>
                </div>
              </Button>
            </> :
            <>
              <Button
                size="md"
                className="rounded-none h-auto w-full md:w-auto !px-3 !py-2 lgh:!px-6 lgh:!py-3 hover:bg-white/90"
                color="white"
                onClick={() => navigate("/checkout/step-2")}
              >
                <div className="flex items-center justify-center">
                  <span className="mr-2 text-sm lgh:text-xl">Try again</span>
                </div>
              </Button>
              <Button
                size="md"
                className="rounded-none h-auto w-full md:w-auto !px-3 !py-2 lgh:!px-6 lgh:!py-3 hover:bg-white/90"
                color="white"
                onClick={() => navigate("/")}
              >
                <div className="flex items-center justify-center">
                  <span className="mr-2 text-sm lgh:text-xl">Home</span>
                </div>
              </Button>
            </>
          }
        </div>
      </div>
    </>

  }
)
